import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIsSuperAdmin } from "@/lib/auth";
import {
  fetchVisitorPrivacySnapshot,
  isPrivacyGateBypassPath,
  shouldBlockPrivacyGate,
  type VisitorPrivacyApi,
} from "@/lib/visitorPrivacy";

/**
 * Loads /api/visitor-privacy-snapshot and applies SPA bypass (LEAD-SAFE):
 * - conversion paths (home DualCTA, invites, checkout) → never blocked, skip fetch
 * - super_admin / geo_sim clean → never blocked
 * - live traffic → never blocked (server + client fail-open; flags analytics-only)
 * - geo_sim anonymous|hosting → overlay for QA on non-conversion routes only
 */
export function useVisitorPrivacyGate() {
  const isSuperAdmin = useIsSuperAdmin();
  const location = useLocation();
  const [snapshot, setSnapshot] = useState<VisitorPrivacyApi | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setReady(true);
      return;
    }
    // Skip heavy gate on admin chrome
    if (location.pathname.startsWith("/admin")) {
      setSnapshot(null);
      setReady(true);
      return;
    }
    // Conversion / home / checkout: skip snapshot entirely — never risk CTA cover
    if (isPrivacyGateBypassPath(location.pathname)) {
      setSnapshot(null);
      setReady(true);
      return;
    }
    const ac = new AbortController();
    setReady(false);
    void (async () => {
      const data = await fetchVisitorPrivacySnapshot(ac.signal);
      if (!ac.signal.aborted) {
        setSnapshot(data);
        setReady(true);
      }
    })();
    return () => ac.abort();
  }, [location.pathname, location.search]);

  const blocked = useMemo(
    () =>
      shouldBlockPrivacyGate({
        snapshot,
        isSuperAdmin,
        pathname: location.pathname,
      }),
    [snapshot, isSuperAdmin, location.pathname],
  );

  return {
    ready,
    snapshot,
    blocked,
    privacy: snapshot?.privacy ?? null,
    blockReason: snapshot?.blockReason ?? null,
    allowlisted: snapshot?.allowlisted === true,
    mode: snapshot?.mode ?? "live",
  };
}
